import { useEffect } from 'react'
import { Box, HStack, Stack, Text, Button, useDisclosure, Tooltip } from '@chakra-ui/react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import SvgIcon from 'components/SvgIcon'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { IAsyncSelectOption } from 'interfaces/common'
import { getValidArray } from 'utils/common'
import FilterDialog from './components/FilterDialog'
import MultipleDropdown from './components/MultipleDropdown'
import { checkValidFilter, getTagOptions } from './utils'
import styles from './styles.module.scss'
import { IDropdown } from 'types/common/select'
import { searchSortByOptions } from 'components/Topbar/constants'
import Dropdown from 'components/Dropdown'
import { DARK_TEXT_MUTED, DARK_TEXT_PRIMARY, secondaryBtnSx } from 'constants/dark-tokens'
import FilterCountBadge from 'components/FilterCountBadge'

interface SearchPageHeaderProps {
  // Opt-in dark-surface styling (default light). SearchPage passes `dark`; ProcessPage's
  // ActionSheet keeps the default light look.
  dark?: boolean
  // Stack / Collection type toggle. Off on the My Stacks list (no keyword), which
  // only ever shows Stacks; a keyword search keeps both types.
  showTypeTabs?: boolean
}

// Navy fill for the active segmented tab — matches ArchivePage FilterTray / ProcessPage
// ActionSheet so the dark Search filter row reads identically to the other dark pages.
const TAB_ACTIVE_BG = '#003366'

const tabSx = (active: boolean) => ({
  h: '32px',
  px: '20px',
  borderRadius: 'full',
  fontSize: '14px',
  fontWeight: 500,
  color: active ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED,
  bg: active ? TAB_ACTIVE_BG : 'transparent',
  border: '1px solid transparent',
  _hover: active
    ? { bg: TAB_ACTIVE_BG, opacity: 0.9 }
    : { bg: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.12)' },
  _focus: { boxShadow: 'none', outline: 'none' },
  _focusVisible: { boxShadow: 'none', outline: 'none' }
})

function SearchPageHeader({ dark = false, showTypeTabs = true }: SearchPageHeaderProps) {
  const { searchStore, organizationStore, tagStore, authStore } = useStores()
  const filterButtonBg = dark ? 'transparent' : 'white'
  const filterButtonTextColor = dark ? DARK_TEXT_PRIMARY : 'gray.700'
  const { organization } = organizationStore
  const { displayTypes = [EEsGlobalSearchCollection.PROCESS], isSearching, searchFilter } = searchStore
  // organizationStore.organization is only hydrated on subdomain tenants — fall
  // back to the active org so the Tag options don't fetch with org 0.
  const organizationId: number = organization?.id ?? authStore.resolvedActiveOrgId ?? 0
  const { isOpen: isOpenFilter, onOpen: onOpenFilter, onClose: onCloseFilter } = useDisclosure()

  function toggleFilterProcess(): void {
    if (displayTypes.includes(EEsGlobalSearchCollection.PROCESS)) {
      searchStore.setDisplayTypes(displayTypes.filter(item => item !== EEsGlobalSearchCollection.PROCESS))
    } else {
      searchStore.setDisplayTypes([...displayTypes, EEsGlobalSearchCollection.PROCESS])
    }
    searchStore.search()
  }

  function toggleFilterCollection(): void {
    if (displayTypes.includes(EEsGlobalSearchCollection.COLLECTION)) {
      searchStore.setDisplayTypes(displayTypes.filter(item => item !== EEsGlobalSearchCollection.COLLECTION))
    } else {
      searchStore.setDisplayTypes([...displayTypes, EEsGlobalSearchCollection.COLLECTION])
    }
    searchStore.search()
  }

  function changeFilterTags(tags: IAsyncSelectOption<number>[]): void {
    searchStore.changeFilterTags(tags)
    searchStore.search()
  }

  function clearFilters(): void {
    searchStore.clearSearchFilter()
    searchStore.search()
  }

  function changeFilterSortOrder(sort: string): void {
    searchStore.changeSearchFilter({ ...searchStore.searchFilter, sort })
    searchStore.search()
  }

  useEffect(() => {
    // Keyed on the org so switching teams refreshes the filter options.
    tagStore.fetchTags({ where: { organizationId }, order: ['name ASC'] })
  }, [organizationId])

  // No extra left inset on the root row: the page already wraps this toolbar in
  // its 16px gutter, and the old 10px on top of that pushed the tabs out of line
  // with the banner and the table below.
  return (
    <Stack flexDirection={{ base: 'column', md: 'row' }} gap={4} spacing={0} width="full">
      <Stack flexDirection={{ base: 'column', md: 'row' }} gap={4} spacing={0} flex={1}>
        <HStack
          spacing={2}
          width={{ base: 'full', md: 'auto' }}
          justifyContent={{ base: 'space-between', md: 'flex-start' }}
          // Without the type tabs this row only holds the mobile filter button, so on
          // desktop it would be an empty flex item whose gap pushes Tag off the edge.
          display={showTypeTabs ? 'flex' : { base: 'flex', md: 'none' }}
        >
          {!showTypeTabs ? null : dark ? (
            // Segmented pill tabs on the dark surface — same look as ArchivePage/ProcessPage.
            // Still a multi-toggle (both types can be active); active = type is shown.
            <HStack spacing="8px">
              <Button
                sx={tabSx(displayTypes.includes(EEsGlobalSearchCollection.PROCESS))}
                onClick={toggleFilterProcess}
              >
                Stack
              </Button>
              <Button
                sx={tabSx(displayTypes.includes(EEsGlobalSearchCollection.COLLECTION))}
                onClick={toggleFilterCollection}
              >
                Collection
              </Button>
            </HStack>
          ) : (
            <>
              <Tooltip
                className={styles.tooltip}
                label="Stack"
                hasArrow
                shouldWrapChildren={true}
                background="#5c5c5c"
                placement="top-start"
              >
                <SvgIcon
                  className={cx(styles.icon, styles.process, {
                    [styles.active]: displayTypes.includes(EEsGlobalSearchCollection.PROCESS)
                  })}
                  onClick={toggleFilterProcess}
                  iconName="process"
                  size={32}
                />
              </Tooltip>
              <Tooltip
                className={styles.tooltip}
                label="Collection"
                hasArrow
                shouldWrapChildren={true}
                background="#5c5c5c"
                placement="top-start"
              >
                <SvgIcon
                  className={cx(styles.icon, styles.collection, {
                    [styles.active]: displayTypes.includes(EEsGlobalSearchCollection.COLLECTION)
                  })}
                  onClick={toggleFilterCollection}
                  iconName="collection"
                  size={32}
                />
              </Tooltip>
            </>
          )}
          {/* Mobile filter trigger — icon only, same pill/icon as the Archive and
              Drafts toolbars so every dark list page reads the same on a phone.
              The active-filter count rides as a badge instead of inline text,
              which kept changing the button's width. */}
          <Box position="relative" display={{ base: 'block', md: 'none' }}>
            <Button
              {...(dark
                ? { sx: secondaryBtnSx(searchStore.countFilter() > 0) }
                : {
                    height: 8,
                    backgroundColor: filterButtonBg,
                    border: `1px solid ${searchStore.countFilter() > 0 ? '#00A9EB' : '#E2E8F0'}`,
                    borderRadius: '8px',
                    padding: '8px',
                    color: filterButtonTextColor
                  })}
              aria-label="Filter"
              onClick={onOpenFilter}
            >
              <SvgIcon
                className={styles.icon}
                iconName={dark ? 'ic_outline-filter-list' : 'sort'}
                size={16}
                color={dark ? DARK_TEXT_MUTED : undefined}
              />
            </Button>
            {searchStore.countFilter() > 0 && <FilterCountBadge count={searchStore.countFilter()} />}
          </Box>
        </HStack>
        <Stack display={{ base: 'none', md: 'block' }} width={{ base: 'full', md: '200px' }}>
          <MultipleDropdown
            width={{ base: 'full', md: '200px' }}
            name="tags"
            items={getValidArray(searchFilter.tags)}
            options={getTagOptions(tagStore.tags)}
            setItems={changeFilterTags}
            placeHolder="Tag"
            isDisabled={isSearching ?? false}
            dark={dark}
          />
        </Stack>
        <HStack spacing={4} justify="space-between" flex={1} display={{ base: 'none', md: 'flex' }}>
          <HStack spacing={4}>
            {/* On the dark surface this is the shared secondary pill, the same one
                 the Drafts, Archive and Assignments toolbars use — it had drifted
                 into its own height, padding, border and icon. */}
            <Button
              {...(dark
                ? { sx: secondaryBtnSx(searchStore.countFilter() > 0) }
                : {
                    height: 8,
                    backgroundColor: filterButtonBg,
                    border: `1px solid ${searchStore.countFilter() > 0 ? '#00A9EB' : '#E2E8F0'}`,
                    borderRadius: '8px',
                    padding: { base: '8px', lg: '10px' },
                    color: filterButtonTextColor
                  })}
              gap={{ base: 0, lg: 2 }}
              fontWeight={500}
              cursor="pointer"
              variant="solid"
              onClick={onOpenFilter}
              isDisabled={isSearching ?? false}
            >
              <SvgIcon
                className={styles.icon}
                iconName={dark ? 'ic_outline-filter-list' : 'sort'}
                size={dark ? 16 : 14}
                color={dark ? DARK_TEXT_MUTED : undefined}
              />
              <Text marginBottom="0" fontWeight={500} fontSize={{ base: '0px', lg: '14px' }} lineHeight="20px">
                {`More Filters ${searchStore.countFilter() > 0 ? `(${searchStore.countFilter()})` : ''}`}
              </Text>
            </Button>
            <Text
              color={filterButtonTextColor}
              fontSize="14px"
              fontWeight={500}
              lineHeight={2}
              cursor="pointer"
              onClick={clearFilters}
              hidden={!checkValidFilter(searchFilter)}
              whiteSpace="nowrap"
            >
              Clear filters
            </Text>
          </HStack>
          <Dropdown
            name="sort"
            titleDropdown="SORT BY"
            fontSize="sm"
            width={{ base: 'full', md: '160px' }}
            height="32px"
            options={searchSortByOptions}
            item={{
              title: searchFilter.sort ? `${searchFilter.sort}` : 'Most relevant',
              value: searchFilter.sort ? `${searchFilter.sort}` : 'Most relevant'
            }}
            setValue={(name: string, dropdownValue: IDropdown) => changeFilterSortOrder(`${dropdownValue.value}`)}
            isDisabled={isSearching ?? false}
            dark={dark}
          />
        </HStack>
      </Stack>
      <FilterDialog isOpen={isOpenFilter} onClose={onCloseFilter} dark={dark} />
    </Stack>
  )
}

export default observer(SearchPageHeader)
